import React from "react";
import {
  Stack,
  Typography,
  Button,
} from "@mui/material";
import { motion } from "framer-motion"; // Import motion for animation

import secton7Image from "./ISO certificate.png";

const CertificateCard = () => {
  // Define the animation variant for a smooth fade-in from the top
  const fadeInFromTopVariant = {
    hidden: { opacity: 0, y: -50 }, // Starts invisible and 50px above its final position
    visible: {
      opacity: 1, // Becomes fully visible
      y: 0, // Moves to its natural Y position
      transition: {
        duration: 0.8, // Animation takes 0.8 seconds
        ease: "easeOut", // A smooth easing function
      },
    },
  };

  return (
    <Stack
      component="section" // Added for semantic correctness
      aria-label="Why Trust Us - Certifications" // Added for accessibility
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        backgroundImage: `url('Bg.svg')`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        py: { xs: 2, sm: 3, md: 3, lg: 3.5 }, // Added responsive padding Y for consistent section spacing
        px: { xs: 2, sm: 3, md: 4, lg: 6 }, // Added responsive padding X for consistent section spacing
        // No overflow hidden here, as the content itself has the background image
      }}
    >

      <motion.div
        initial="hidden" // Set the initial animation state
        whileInView="visible" // Animate to 'visible' when the component enters the viewport
        viewport={{ once: true, amount: 0.3 }} // Animation plays once when 30% of the element is visible
        variants={fadeInFromTopVariant} // Apply the defined animation variant
        style={{
          width: "100%", // Ensures the motion.div takes full available width
          maxWidth: "1280px", // Consistent max-width for content alignment
          display: "flex", // Needed for its child Stack to be properly aligned
          justifyContent: "center", // Centers the child Stack within the maxWidth
          // REMOVED: borderRadius and boxShadow from here, as requested.
          // The background image container (the inner Stack) will handle its own borderRadius.
        }}
      >
        <Stack
          // This Stack contains the bg1.svg background and its own borderRadius.
          // Its width is now effectively controlled by the parent motion.div's maxWidth.
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: '100%', // Ensures it fills the motion.div horizontally
            backgroundColor: "transparent",
            backgroundImage: `url('bg1.svg')`,
            backgroundRepeat: "no-repeat",
            backgroundSize: {
              xl: "100% 80%",
              lg: "100% 80%",
              md: "100% 90%",
              sm: "100% 95%",
              xs: "100% 100%",
            },
            backgroundPosition: "center",
            borderRadius: "8px", // This borderRadius is kept as it was in your original code
            padding: {
              lg: "44px",
              md: "44px",
              sm: "20px",
              xs: "14px",
            },
          }}
        >
          <Stack
            sx={{
              display: "flex",
              flexDirection: {
                lg: "row",
                xl: "row",
                md: "row",
                sm: "column",
                xs: "column",
              },
              justifyContent: "center",
              alignItems: "center",
              height: "auto",
              gap: { xs: "16px", sm: "24px", md: "32px" },
            }}
          >
            {/* Left section: Text content */}
            <Stack
              sx={{
                color: "white",
                width: { lg: "50%", xl: "50%", sm: "100%", xs: "100%" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: {
                  lg: "flex-start",
                  md: "flex-start",
                  sm: "center",
                  xs: "center",
                },
                textAlign: {
                  lg: "left",
                  md: "left",
                  sm: "center",
                  xs: "center",
                },
                marginRight: { lg: "22px", md: "16px", sm: "0px", xs: "0px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: {
                    lg: "24px",
                    xl: "24px",
                    sm: "18px",
                    xs: "20px",
                  },
                  lineHeight: {
                    lg: "32px",
                    xl: "32px",
                    sm: "28px",
                    xs: "24px",
                  },
                  fontWeight: "400",
                  color: "white",
                  marginBottom: { lg: "24px", xl: "24px", md: "20px", sm: "16px", xs: "16px" },
                  fontFamily: "'Roboto', sans-serif",
                  marginTop: { lg: "0px", xl: "0px", sm: "0px", xs: "16px" },
                }}
              >
                Why do top brands trust us?
              </Typography>

              <Typography
                sx={{
                  fontSize: {
                    lg: "26px",
                    xl: "26px",
                    sm: "26px",
                    xs: "18px",
                  },
                  lineHeight: {
                    lg: "1.4",
                    xl: "1.4",
                    sm: "1.5",
                    xs: "1.6",
                  },
                  fontWeight: "600",
                  color: "white",
                  marginBottom: { lg: "46px", xl: "46px", md: "32px", sm: "24px", xs: "24px" },
                  fontFamily: "'Roboto', sans-serif",
                  marginTop: { lg: "-4px", xl: "-4px", md: "-2px", sm: "-2px", xs: "-2px" },
                  textAlign: "left",
                  marginLeft: {
                    xl: "0px",
                    lg: "0px",
                    md: "0px",
                    sm: "0px",
                    xs: "20px",
                  },
                  marginRight: {
                    xl: "0px",
                    lg: "0px",
                    md: "0px",
                    sm: "0px",
                    xs: "0px"
                  },
                }}
              >
                We are built on a foundation of certified quality, robust security, and a commitment to excellence in everything we do.
              </Typography>

              <Stack
                sx={{
                  display: "flex",
                  flexDirection: {
                    lg: "row",
                    xl: "row",
                    xs: "column",
                    sm: "column",
                  },
                  justifyContent: {
                    lg: "flex-start",
                    md: "flex-start",
                    sm: "center",
                    xs: "center",
                  },
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#ffffff",
                    marginRight: { lg: "16px", sm: "0px" },
                    marginBottom: {
                      lg: "0px",
                      xl: "0px",
                      xs: "16px",
                      sm: "16px",
                      md: "16px",
                    },
                    width: {
                      lg: "auto",
                      xl: "auto",
                      sm: "90%",
                      xs: "90%",
                    },
                    borderRadius: "8px",
                    display: "none",
                  }}
                  size="large"
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "rgb(0, 0, 0)",
                      textTransform: "none",
                      paddingRight: "4px",
                      fontFamily: "'Roboto', sans-serif",
                    }}
                  >
                    Get started -
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "rgb(0, 0, 0)",
                      textTransform: "none",
                      fontFamily: "'Roboto', sans-serif",
                    }}
                  >
                    it's free
                  </Typography>
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    fontSize: "16px",
                    fontWeight: "500",
                    color: "#ffffff",
                    borderColor: "#ffffff",
                    "&:hover": {
                      color: "#ffffff",
                      borderColor: "#ffffff",
                    },
                    width: {
                      lg: "auto",
                      xl: "auto",
                      sm: "90%",
                      xs: "90%",
                    },
                    fontFamily: "'Roboto', sans-serif",
                    borderRadius: "8px",
                    display: "none",
                  }}
                  size="large"
                >
                  Talk to human
                </Button>
              </Stack>
            </Stack>

            {/* Right section: Image */}
            <Stack
              sx={{
                width: {
                  lg: "50%",
                  xl: "50%",
                  sm: "100%",
                  xs: "90%",
                  md: "50%",
                },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "auto",
                marginTop: { sm: "0", xs: "-36px" }
              }}
            >
              <img
                src={secton7Image}
                alt="ISO certificate"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </motion.div> {/* End of motion.div wrapping the main content */}
    </Stack>
  );
};

export default CertificateCard;